// OWNER: FRONTEND-ADMIN. SPEC §15.6 Reports: funnel table (group by week/campaign/user), hour-of-day connect rate,
// today's leaderboard, date range picker, CSV export. Setters see only their own rows (enforced server-side).
import type { ComponentChildren } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import type { FunnelRow } from '../../../src/shared/api';
import { Empty, ErrorBanner, pct } from '../components/ui';
import { useMe } from '../me';
import { adminApi } from '../admin/endpoints';
import { FUNNEL_METRIC_COLUMNS, funnelColumns, type FunnelColumnKey } from '../admin/layout';
import { Check, Field, FixedHead, Stat, downloadText, inputValue, useLoad } from '../admin/ui';
import { OPS_TZ, RANGE_PRESETS, presetRange, ymdInTz, type RangePreset } from '../admin/dates';
import { funnelCsv, hourLabel, hourSeries, totalShowRateUnknown, totalsRow, type GroupKey } from '../admin/report';
import { TeamTracking } from '../admin/TeamTracking'; // setter tracking (SPEC §19, 2026-10-01)

const LOW_SAMPLE = 20; // hours with fewer dials are drawn hatched: too few to trust the rate

export function Reports(_props: { params: Record<string, string> }) {
  const me = useMe();
  const today = ymdInTz(new Date(), OPS_TZ);
  const [preset, setPreset] = useState<RangePreset | 'custom'>('this_week');
  const [range, setRange] = useState(presetRange('this_week', today));
  const [groupBy, setGroupBy] = useState<GroupKey[]>(['week', 'user']);
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const campaigns = useLoad(() => adminApi.campaigns(), []);
  const validRange = !!range.from && !!range.to && range.from <= range.to;
  const groupParam = (['week', 'campaign', 'user'] as GroupKey[]).filter((g) => groupBy.includes(g)).join(',');

  const funnel = useLoad(
    () => (validRange ? adminApi.funnel({ from: range.from, to: range.to, groupBy: groupParam, campaignId: campaignId || undefined }) : Promise.resolve([] as FunnelRow[])),
    [range.from, range.to, groupParam, campaignId],
  );
  const hours = useLoad(
    () => (validRange ? adminApi.hours({ from: range.from, to: range.to, campaignId: campaignId || undefined }) : Promise.resolve([])),
    [range.from, range.to, campaignId],
  );
  const todayReport = useLoad(() => adminApi.today(), []);

  const pickPreset = (p: RangePreset) => { setPreset(p); setRange(presetRange(p, today)); };
  const toggleGroup = (g: GroupKey, on: boolean) => setGroupBy(on ? [...groupBy, g] : groupBy.filter((x) => x !== g));

  return (
    <div class="adm-page">
      <div class="adm-head">
        <div>
          <h1>Reports</h1>
          <div class="adm-sub">Dates are Toronto calendar days (ops time); hours of day are the prospect's local time.{me.role === 'admin' ? '' : ' You see your own numbers.'}</div>
        </div>
      </div>

      <TeamTracking />

      <TodayPanel data={todayReport.data} error={todayReport.error} />

      <div class="card stack">
        <div class="adm-toolbar">
          {RANGE_PRESETS.map((p) => <button class={`btn btn-sm ${preset === p.key ? 'btn-ink' : ''}`} onClick={() => pickPreset(p.key)}>{p.label}</button>)}
          <span class="grow" />
          <Field label="From"><input type="date" value={range.from} max={range.to} onInput={(e) => { setPreset('custom'); setRange({ ...range, from: inputValue(e) }); }} /></Field>
          <Field label="To"><input type="date" value={range.to} min={range.from} onInput={(e) => { setPreset('custom'); setRange({ ...range, to: inputValue(e) }); }} /></Field>
          <Field label="Campaign">
            <select value={String(campaignId)} onChange={(e) => { const v = inputValue(e); setCampaignId(v ? Number(v) : ''); }}>
              <option value="">All campaigns</option>
              {(campaigns.data ?? []).map((c) => <option value={String(c.id)}>{c.name}</option>)}
            </select>
          </Field>
        </div>
        {!validRange ? <div class="chip chip-bad" style="display:block;padding:8px 10px">Pick a start date on or before the end date.</div> : null}
      </div>

      <FunnelTable rows={funnel.data ?? []} loading={funnel.loading} error={funnel.error} groupBy={groupBy} onToggle={toggleGroup} from={range.from} to={range.to} />

      <HourChart buckets={hours.data ?? []} error={hours.error} loading={hours.loading} />
    </div>
  );
}

function TodayPanel(props: { data: import('../../../src/shared/api-core').TodayReport | null; error: string | null }) {
  const d = props.data;
  return (
    <div class="adm-grid-2">
      <div class="card stack">
        <h3>My day</h3>
        <ErrorBanner error={props.error} />
        {d ? (
          <div class="row" style="flex-wrap:wrap;gap:10px">
            <Stat label="dials" value={d.mine.dials} />
            <Stat label="connects" value={d.mine.connects} />
            <Stat label="DM conversations" value={d.mine.dmConversations} />
            <Stat label="meetings booked" value={d.mine.meetingsBooked} />
            <Stat label="talk minutes" value={d.mine.talkMinutes} />
          </div>
        ) : null}
      </div>
      <div class="card stack">
        <h3>Today's leaderboard</h3>
        {d && d.leaderboard.length === 0 ? <div class="muted">No dials yet today.</div> : null}
        {d && d.leaderboard.length ? (
          <table>
            <thead><tr><th>#</th><th>Setter</th><th class="num">Dials</th><th class="num">Connects</th><th class="num">DM convos</th><th class="num">Meetings</th></tr></thead>
            <tbody>{[...d.leaderboard].sort((a, b) => b.meetings - a.meetings || b.dmConversations - a.dmConversations || b.dials - a.dials).map((r, i) => (
              <tr><td>{i + 1}</td><td>{r.name}</td><td class="num">{r.dials}</td><td class="num">{r.connects}</td><td class="num">{r.dmConversations}</td><td class="num"><b>{r.meetings}</b></td></tr>
            ))}</tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}

function FunnelTable(props: { rows: FunnelRow[]; loading: boolean; error: string | null; groupBy: GroupKey[]; onToggle: (g: GroupKey, on: boolean) => void; from: string; to: string }) {
  const { rows, groupBy } = props;
  const sorted = useMemo(() => [...rows].sort((a, b) =>
    (groupBy.includes('week') ? b.week.localeCompare(a.week) : 0)
    || (a.campaignName ?? '').localeCompare(b.campaignName ?? '')
    || (a.userName ?? '').localeCompare(b.userName ?? '')), [rows, groupBy.join()]);
  const total = rows.length > 1 ? totalsRow(rows) : null;
  const showUnknown = totalShowRateUnknown(rows);
  const cols = funnelColumns(groupBy);
  const sub = (text: string, title?: string) => <span class="adm-cell-sub" title={title}>{text}</span>;
  const cells = (r: FunnelRow, isTotal = false): Record<FunnelColumnKey, ComponentChildren> => ({
    week: <span class="mono">{r.week}</span>,
    campaign: r.campaignName ?? '—',
    user: r.userName ?? '—',
    range: `${props.from} → ${props.to}`,
    dials: r.dials.toLocaleString(),
    connects: <>{r.connects.toLocaleString()}{sub(pct(r.connectRate), 'connect rate')}</>,
    dm: r.dmConversations.toLocaleString(),
    booked: <>{r.meetingsBooked}{sub(`set ${pct(r.setRate)}`, 'set rate: booked ÷ DM conversations')}</>,
    held: <>{r.meetingsHeld}{isTotal && showUnknown
      ? sub('show n/m', 'Some rows only have no-shows, so the combined show rate can’t be computed exactly.')
      : sub(`show ${pct(r.showRate, 0)}`, 'show rate: held ÷ (held + no-show)')}</>,
    dialsPerMeeting: r.dialsPerMeeting === null ? '—' : r.dialsPerMeeting.toFixed(1),
    vmLive: r.vmLive.toLocaleString(),
    vmDropped: r.vmDropped.toLocaleString(),
    talk: r.talkMinutes.toFixed(1),
  });
  const groupCount = cols.length - FUNNEL_METRIC_COLUMNS.length;
  return (
    <div class="card stack">
      <div class="adm-toolbar">
        <h3 style="margin:0">Funnel</h3>
        <span class="eyebrow" style="margin-left:12px">Group by</span>
        <Check label="Week" checked={groupBy.includes('week')} onChange={(on) => props.onToggle('week', on)} />
        <Check label="Campaign" checked={groupBy.includes('campaign')} onChange={(on) => props.onToggle('campaign', on)} />
        <Check label="Setter" checked={groupBy.includes('user')} onChange={(on) => props.onToggle('user', on)} />
        <span class="grow" />
        {props.loading ? <span class="adm-sub">Loading…</span> : null}
        <button class="btn btn-sm" disabled={rows.length === 0}
          onClick={() => downloadText(`valistix-funnel-${props.from}-to-${props.to}.csv`, funnelCsv(sorted, groupBy, props.from, props.to))}>Export CSV</button>
      </div>
      <ErrorBanner error={props.error} />
      {!props.loading && !props.error && rows.length === 0 ? <Empty>No dials in this range.</Empty> : null}
      {rows.length ? (
        <div class="adm-table-wrap">
          <table class="adm-fixed">
            <FixedHead cols={cols} />
            <tbody>
              {sorted.map((r) => {
                const c = cells(r);
                return <tr>{cols.map((col) => <td class={col.num ? 'num' : undefined}>{c[col.key]}</td>)}</tr>;
              })}
              {total ? (() => {
                const c = cells(total, true);
                return (
                  <tr class="adm-total">
                    <td colSpan={groupCount}>Total</td>
                    {cols.slice(groupCount).map((col) => <td class={col.num ? 'num' : undefined}>{c[col.key]}</td>)}
                  </tr>
                );
              })() : null}
            </tbody>
          </table>
        </div>
      ) : null}
      <div class="adm-sub">
        Connect = a person answered (any human disposition); the connect rate sits under the count. DM conversation = spoke to the decision-maker.
        Set = meetings booked ÷ DM conversations. Show = held ÷ (held + no-show). “n/m” = not measurable from the grouped rows. The CSV has every rate in its own column.
      </div>
    </div>
  );
}

function HourChart(props: { buckets: Array<{ localHour: number; dials: number; connects: number; meetings: number }>; error: string | null; loading: boolean }) {
  const series = hourSeries(props.buckets);
  const maxRate = Math.max(0.05, ...series.map((s) => s.rate ?? 0));
  const [table, setTable] = useState(false);
  const totalDials = series.reduce((a, s) => a + s.dials, 0);
  return (
    <div class="card stack">
      <div class="adm-toolbar">
        <h3 style="margin:0">Connect rate by prospect-local hour</h3>
        <span class="grow" />
        {props.loading ? <span class="adm-sub">Loading…</span> : null}
        <Check label="Show as table" checked={table} onChange={setTable} />
      </div>
      <ErrorBanner error={props.error} />
      {totalDials === 0 && !props.loading ? <div class="muted">No dials in this range.</div> : null}
      {totalDials > 0 && !table ? (
        <div>
          <div class="adm-bars" role="img" aria-label="Connect rate by hour of day">
            {series.map((s) => {
              const h = s.rate === null ? 0 : Math.max(1, Math.round((s.rate / maxRate) * 100));
              return (
                <div class={`adm-bar${s.dials > 0 && s.dials < LOW_SAMPLE ? ' low' : ''}`} tabIndex={0} aria-label={`${hourLabel(s.localHour)}: ${s.dials ? `${pct(s.rate)} connect rate, ${s.dials} dials` : 'no dials'}`}>
                  {s.dials > 0 ? <i style={`height:${h}%`} /> : null}
                  <span class="adm-tip">{hourLabel(s.localHour)} · {s.dials ? `${pct(s.rate)} connect · ${s.connects}/${s.dials} dials · ${s.meetings} mtg` : 'no dials'}</span>
                </div>
              );
            })}
          </div>
          <div class="adm-bars-x">{series.map((s) => <span>{hourLabel(s.localHour)}</span>)}</div>
          <div class="adm-bars-v">{series.map((s) => <span title="dials">{s.dials || ''}</span>)}</div>
          <div class="adm-sub" style="margin-top:6px">Bar height = connects ÷ dials (tallest = {pct(maxRate, 0)}). Numbers under the hours are dials. Hatched bars have fewer than {LOW_SAMPLE} dials: too few to trust.</div>
        </div>
      ) : null}
      {totalDials > 0 && table ? (
        <table>
          <thead><tr><th>Hour</th><th class="num">Dials</th><th class="num">Connects</th><th class="num">Connect %</th><th class="num">Meetings</th></tr></thead>
          <tbody>{series.filter((s) => s.dials > 0).map((s) => (
            <tr><td>{hourLabel(s.localHour)}</td><td class="num">{s.dials}</td><td class="num">{s.connects}</td><td class="num">{pct(s.rate)}</td><td class="num">{s.meetings}</td></tr>
          ))}</tbody>
        </table>
      ) : null}
    </div>
  );
}
